import { FlatList } from "react-native";
import type { ListRenderItemInfo } from "react-native";
import { graphql, useLazyLoadQuery } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { SelectDashboardListQuery } from "./__generated__/SelectDashboardListQuery.graphql";
import { AHText, ListItem, AHIcon, AHSeparator } from "arkh";
import { useCallback, useMemo } from "react";
import { FormattedMessage } from "react-intl";

interface Props {
  dashboardId: string | undefined;
  setDashboardId: (dashboardId: string | undefined) => void;
  addDashboard?: () => void;
}

export const SelectDashboardList = (props: Props) => {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerSelectDashboardList {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
};

const InnerSelectDashboardList = ({
  dashboardId,
  setDashboardId,
  addDashboard,
  fetchKey,
}: Props & { fetchKey: string }) => {
  const data = useLazyLoadQuery<SelectDashboardListQuery>(
    graphql`
      query SelectDashboardListQuery {
        store {
          dashboards {
            id
            name
          }
        }
      }
    `,
    {},
    {
      fetchKey,
    }
  );

  const listData = useMemo(() => data.store.dashboards, [data]);

  const renderItem = useCallback(
    ({ item, index }: ListRenderItemInfo<(typeof listData)[number]>) => {
      return (
        <ListItem
          isFirst={index === 0}
          isLast={!addDashboard && index === listData.length - 1}
          border="border-primary"
          right={
            <ListItem.SingleSelectIndicator
              selected={item.id === dashboardId}
            />
          }
          onPress={() =>
            dashboardId === item.id
              ? setDashboardId(undefined)
              : setDashboardId(item.id)
          }
        >
          <AHText type="list-item-title">{item.name}</AHText>
        </ListItem>
      );
    },
    [addDashboard, listData.length, dashboardId, setDashboardId]
  );

  return (
    <FlatList
      data={listData}
      renderItem={renderItem}
      keyExtractor={(item) => item.id}
      ListFooterComponent={
        addDashboard ? (
          <>
            {listData.length > 0 ? <AHSeparator /> : null}
            <ListItem
              isLast
              isFirst={listData.length === 0}
              right={<AHIcon type="add-circle" size="large" color="primary" />}
              border="border-primary"
              onPress={addDashboard}
            >
              <AHText type="list-item-title">
                <FormattedMessage
                  description="StorecastAdminAddTVConfigureAssignDashboard add new dashboard"
                  defaultMessage="Create a new dashboard"
                />
              </AHText>
            </ListItem>
          </>
        ) : null
      }
      ItemSeparatorComponent={AHSeparator}
    />
  );
};
